import { useEffect, useMemo, useCallback } from 'react'
import { Control, Controller, useWatch, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Shield, Settings2 } from 'lucide-react'

import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getActiveSLATemplates } from '@/modules/Service/services/sla'
import { Contract, ContractServiceSLA } from '@/modules/Contract/types'

// Demo templates
const DEMO_TEMPLATES = [
    {
        sla_template_id: 1,
        template_name: 'Bronze (99%)',
        uptime: 99.0,
        downtime: 438,
        business_hours: true,
        response_time: 480, // 8 hours
        resolution_time: 1440, // 24 hours
    },
    {
        sla_template_id: 2,
        template_name: 'Silver (99.5%)',
        uptime: 99.5,
        downtime: 216,
        business_hours: true,
        response_time: 240, // 4 hours
        resolution_time: 720, // 12 hours
    },
    {
        sla_template_id: 3,
        template_name: 'Gold (99.9%)',
        uptime: 99.9,
        downtime: 43,
        business_hours: false,
        response_time: 120, // 2 hours
        resolution_time: 480, // 8 hours
    },
    {
        sla_template_id: 4,
        template_name: 'Platinum (99.99%)',
        uptime: 99.99,
        downtime: 4,
        business_hours: false,
        response_time: 60, // 1 hour
        resolution_time: 240, // 4 hours
    },
]

const CUSTOM_OPTION_VALUE = 'custom'

// Format minutes to readable string
const formatMinutes = (minutes: number | undefined): string => {
    if (!minutes) return ''
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return mins > 0 ? `${hours}h${mins}p` : `${hours}h`
    }
    return `${minutes}p`
}

interface Props {
    control: Control<Contract>
    setValue: UseFormSetValue<Contract>
}

const SLAConfigSection: React.FC<Props> = ({ control, setValue }) => {
    const { t } = useTranslation()
    const { setModal } = useModal()

    const slaEnabled = useWatch({ control, name: 'sla_enable' })
    const selectedTemplateId = useWatch({
        control,
        name: 'service_sla.sla_template_id',
    })
    const slaSource = useWatch({ control, name: 'service_sla.sla_source' })
    const uptime = useWatch({ control, name: 'service_sla.uptime_guarantee' })
    const downtime = useWatch({
        control,
        name: 'service_sla.downtime_allowance_minutes',
    })
    const businessHoursOnly = useWatch({
        control,
        name: 'service_sla.business_hours_only',
    })
    const responseTime = useWatch({
        control,
        name: 'service_sla.sla_rules.0.response_time_minutes',
    })
    const resolutionTime = useWatch({
        control,
        name: 'service_sla.sla_rules.0.resolution_time_minutes',
    })
    const serviceSLA = useWatch({ control, name: 'service_sla' })

    const isCustom = slaSource === 'custom'

    const { data: apiTemplates = [] } = useQueryWithHandler({
        queryKey: ['sla-templates-active'],
        queryFn: getActiveSLATemplates,
    })

    const templates = useMemo(
        () =>
            apiTemplates.length > 0
                ? apiTemplates.map((t) => ({
                      sla_template_id: t.sla_template_id,
                      template_name:
                          t.uptime_guarantee != null
                              ? `${t.template_name || 'Template'} (${t.uptime_guarantee}%)`
                              : t.template_name || 'Template',
                      uptime: t.uptime_guarantee,
                      downtime: t.downtime_allowance_minutes,
                      business_hours: t.business_hours_only,
                      response_time: t.default_response_time,
                      resolution_time: t.default_resolution_time,
                  }))
                : DEMO_TEMPLATES,
        [apiTemplates]
    )

    // Auto-fill when template selected
    useEffect(() => {
        if (selectedTemplateId && !isCustom) {
            const t = templates.find(
                (t) => t.sla_template_id === selectedTemplateId
            )
            if (t) {
                setValue('service_sla.uptime_guarantee', t.uptime)
                setValue('service_sla.downtime_allowance_minutes', t.downtime)
                setValue('service_sla.business_hours_only', t.business_hours)
                setValue('service_sla.sla_source', 'template')
                // Set response/resolution times
                if (t.response_time || t.resolution_time) {
                    setValue('service_sla.sla_rules', [
                        {
                            ticket_type_group: 'default',
                            response_time_minutes: t.response_time,
                            resolution_time_minutes: t.resolution_time,
                        },
                    ])
                }
            }
        }
    }, [selectedTemplateId, templates, setValue, isCustom])

    const handleSaveCustom = useCallback(
        (data: Partial<ContractServiceSLA>) => {
            setValue('service_sla.sla_source', 'custom')
            setValue('service_sla.sla_template_id', undefined)
            setValue('service_sla.uptime_guarantee', data.uptime_guarantee)
            setValue(
                'service_sla.downtime_allowance_minutes',
                data.downtime_allowance_minutes
            )
            setValue(
                'service_sla.business_hours_only',
                data.business_hours_only
            )
            setValue(
                'service_sla.business_hours_config',
                data.business_hours_config
            )
            setValue('service_sla.sla_rules', data.sla_rules)
        },
        [setValue]
    )

    const openCustomModal = useCallback(() => {
        setModal('ContractModals/SLAConfig/index', {
            defaultValues: serviceSLA,
            onSave: handleSaveCustom,
        })
    }, [setModal, serviceSLA, handleSaveCustom])

    const handleSelectChange = (value: string) => {
        if (value === CUSTOM_OPTION_VALUE) {
            openCustomModal()
        } else {
            setValue('service_sla.sla_source', 'template')
            setValue('service_sla.sla_template_id', parseInt(value))
        }
    }

    const selectValue = isCustom
        ? CUSTOM_OPTION_VALUE
        : selectedTemplateId
          ? selectedTemplateId.toString()
          : undefined
    const selectedTemplate = templates.find(
        (t) => t.sla_template_id === selectedTemplateId
    )

    return (
        <div
            data-tour="sla-config-section"
            className="border border-blue-200 bg-blue-50/30 rounded-md overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center gap-2 p-3">
                <Controller
                    name="sla_enable"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="scale-75"
                        />
                    )}
                />
                <Shield className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">SLA</span>
            </div>

            {/* Content */}
            <div className="border-t border-blue-200 p-3 bg-white/50 space-y-3">
                {/* Template selector */}
                <div className="flex items-center gap-2">
                    <Select
                        value={selectValue}
                        onValueChange={handleSelectChange}
                        disabled={!slaEnabled}
                    >
                        <SelectTrigger className="h-8 flex-1 text-xs">
                            <SelectValue
                                placeholder={t('select_sla_template')}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {templates.map((tpl) => (
                                <SelectItem
                                    key={tpl.sla_template_id}
                                    value={tpl.sla_template_id.toString()}
                                >
                                    {tpl.template_name}
                                </SelectItem>
                            ))}
                            <SelectItem value={CUSTOM_OPTION_VALUE}>
                                <span className="text-orange-600 flex items-center gap-1">
                                    <Settings2 className="w-3 h-3" />
                                    {t('sla_customize')}
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {isCustom && slaEnabled && (
                        <button
                            type="button"
                            onClick={openCustomModal}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                        >
                            {t('sla_edit')}
                        </button>
                    )}
                </div>

                {/* Hint when no config yet */}
                {slaEnabled && !isCustom && !selectedTemplate && (
                    <p className="text-[10px] text-gray-400 italic">
                        {t('sla_config_optional_hint')}
                    </p>
                )}

                {/* Summary badges */}
                {(isCustom || selectedTemplate) &&
                    (uptime != null || downtime != null) && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {uptime != null && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-gray-500">
                                        {t('sla_uptime')}:
                                    </span>
                                    <Badge
                                        variant="secondary"
                                        className="text-[10px] h-5 px-1.5 bg-green-100 text-green-700"
                                    >
                                        {uptime}%
                                    </Badge>
                                </div>
                            )}
                            {downtime != null && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-gray-500">
                                        {t('sla_downtime')}:
                                    </span>
                                    <Badge
                                        variant="secondary"
                                        className="text-[10px] h-5 px-1.5 bg-orange-100 text-orange-700"
                                    >
                                        {t('sla_downtime_per_month', {
                                            minutes: downtime,
                                        })}
                                    </Badge>
                                </div>
                            )}
                            {responseTime != null && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-gray-500">
                                        {t('sla_response_time')}:
                                    </span>
                                    <Badge
                                        variant="secondary"
                                        className="text-[10px] h-5 px-1.5 bg-purple-100 text-purple-700"
                                    >
                                        {formatMinutes(responseTime)}
                                    </Badge>
                                </div>
                            )}
                            {resolutionTime != null && (
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-gray-500">
                                        {t('sla_resolution_time')}:
                                    </span>
                                    <Badge
                                        variant="secondary"
                                        className="text-[10px] h-5 px-1.5 bg-cyan-100 text-cyan-700"
                                    >
                                        {formatMinutes(resolutionTime)}
                                    </Badge>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-500">
                                    {t('sla_apply')}:
                                </span>
                                <Badge
                                    variant="secondary"
                                    className="text-[10px] h-5 px-1.5 bg-blue-100 text-blue-700"
                                >
                                    {businessHoursOnly
                                        ? t('sla_business_hours')
                                        : t('sla_24_7')}
                                </Badge>
                            </div>
                        </div>
                    )}

                {/* SLA Legend - Chú giải */}
                {slaEnabled && (
                    <div className="mt-2 pt-2 border-t border-blue-100">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                            <div className="flex items-start gap-1">
                                <span className="text-green-600 font-medium">
                                    Uptime:
                                </span>
                                <span className="text-gray-500">
                                    % thời gian dịch vụ hoạt động
                                </span>
                            </div>
                            <div className="flex items-start gap-1">
                                <span className="text-orange-600 font-medium">
                                    Downtime:
                                </span>
                                <span className="text-gray-500">
                                    Thời gian ngừng hoạt động tối đa/tháng
                                </span>
                            </div>
                            <div className="flex items-start gap-1">
                                <span className="text-purple-600 font-medium">
                                    Response:
                                </span>
                                <span className="text-gray-500">
                                    Thời gian phản hồi ticket
                                </span>
                            </div>
                            <div className="flex items-start gap-1">
                                <span className="text-cyan-600 font-medium">
                                    Resolution:
                                </span>
                                <span className="text-gray-500">
                                    Thời gian xử lý ticket
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SLAConfigSection
